import { createTheme } from '@mui/material/styles'

// Palette values MUST be real, resolvable colors, not var() strings.
// MUI doesn't just decompose `main` once at createTheme() (which an earlier
// version of this file worked around) — many components call alpha() /
// decomposeColor() on theme.palette.* directly at render time (Button's
// hover/focus/disabled overlays, ripples, etc.), and that throws on a
// var() string regardless of what createTheme() already resolved. These
// hex values are mirrored from styles/tokens/colors.css by hand — update
// both if the brand manual changes. Component-level `sx` (see Button,
// Card) keeps using var(--token) directly instead: that's plain CSS, never
// parsed by MUI's JS, so it still updates live under a tenant-branding
// var() override — only this palette object loses that.
export const muiTheme = createTheme({
  palette: {
    primary: {
      main: '#347ABD', // --color-brand / --vo-celeste
      dark: '#335AA5', // --color-brand-strong / --vo-cobalto — matches legacy Button hover
      contrastText: '#FFFFFF', // --text-on-brand
    },
    // Spec assigns no explicit secondary token; the brand accent tone is
    // the closest existing token and is used here as a judgment call.
    secondary: {
      main: '#38B6EA', // --color-brand-accent / --vo-capri
    },
    error: {
      main: '#D64545', // --vo-danger
    },
    warning: {
      main: '#E5A100', // --vo-warning
      contrastText: '#15213C', // --text-strong / --vo-ink
    },
    success: {
      main: '#1F9D6B', // --vo-success
    },
    info: {
      main: '#347ABD', // --vo-info (alias of --vo-celeste)
    },
    text: {
      primary: '#15213C', // --text-strong / --vo-ink
      secondary: '#3C4A68', // --text-body / --vo-ink-2
      disabled: '#6B7A9B', // --text-muted / --vo-slate
    },
    background: {
      default: '#F4F6FC', // --surface-page / --vo-mist
      paper: '#FFFFFF', // --surface-card / --vo-white
    },
    divider: '#D8E0EE', // --border-subtle / --vo-line
    // The legacy DefaultButton dimmed disabled buttons by wrapping the whole
    // element at opacity: 0.6, so a disabled primary button stayed brand-blue
    // (just lighter), never MUI's stock neutral grey. disabledBackground is a
    // hand-computed 60% blend of primary.main (#347ABD) over white — the same
    // math CSS opacity would produce against a white page/card backdrop — so
    // contained buttons keep a blue-tinted disabled look, and outlined buttons
    // (which use this token for their border) get a blue-tinted border instead
    // of grey. action.disabled (text) is left at MUI's default: a flat token
    // can't vary per variant, and MUI's muted grey stays legible on both a
    // light-blue contained background and a white outlined background, unlike
    // a hardcoded white would (invisible on light backgrounds for secondary).
    action: {
      disabledBackground: '#85AFD7',
      disabledOpacity: 0.6, // was MUI's 0.38 — matches DefaultButton's dimming factor for Checkbox/Radio/Switch once those migrate
    },
  },
  typography: {
    fontFamily: 'var(--font-body)',
    h1: { fontFamily: 'var(--font-primary)', fontSize: 'var(--fs-h1)' },
    h2: { fontFamily: 'var(--font-primary)', fontSize: 'var(--fs-h2)' },
    h3: { fontFamily: 'var(--font-primary)', fontSize: 'var(--fs-h3)' },
    h4: { fontFamily: 'var(--font-primary)', fontSize: 'var(--fs-h4)' },
    button: {
      fontFamily: 'var(--font-secondary)',
      textTransform: 'none',
    },
  },
  shape: {
    // Plain number, not a var() string: MUI does internal
    // `borderRadius * factor` math that requires a real number. Mirrors
    // --radius-md (10px) — keep in sync with spacing.css by hand.
    borderRadius: 10,
  },
})
